import React, { useState } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import {
  Workflow,
  Bot,
  MessageCircle,
  Users,
  UserCheck,
  Plug,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Zap,
  Shield,
  CalendarClock,
  Cpu,
  Server,
  Sparkles,
} from "lucide-react";

const ACCENT = "#6366f1";
const SUCCESS = "#22c55e";
const WARN = "#eab308";
const DANGER = "#ef4444";

const sparkData: Record<string, number[]> = {
  workflows: [6, 7, 8, 9, 10, 11, 12],
  agents: [4, 5, 6, 6, 7, 7, 8],
  chatbots: [3, 4, 4, 4, 5, 5, 5],
  users: [14, 15, 16, 17, 17, 18, 18],
  activeUsers: [8, 9, 10, 11, 12, 9, 14],
  mcp: [2, 3, 3, 4, 4, 5, 5],
};

const categoryStats = [
  { key: "workflows", label: "Workflows", value: 12, change: 8, trend: "up" as const, icon: Workflow },
  { key: "agents", label: "Agents", value: 8, change: 5, trend: "up" as const, icon: Bot },
  { key: "chatbots", label: "ChatBots", value: 5, change: -1, trend: "down" as const, icon: MessageCircle },
  { key: "users", label: "Users", value: 18, change: 6, trend: "up" as const, icon: Users },
  { key: "activeUsers", label: "Active Users", value: 14, change: 12, trend: "up" as const, icon: UserCheck },
  { key: "mcp", label: "MCP", value: 5, change: 2, trend: "up" as const, icon: Plug },
];

// Workflow runs (auto) – not user-triggered
const workflowRunsData = [
  { day: "Mon", runs: 42, success: 40, failed: 2 },
  { day: "Tue", runs: 51, success: 48, failed: 3 },
  { day: "Wed", runs: 48, success: 46, failed: 2 },
  { day: "Thu", runs: 59, success: 56, failed: 3 },
  { day: "Fri", runs: 62, success: 60, failed: 2 },
  { day: "Sat", runs: 38, success: 36, failed: 2 },
  { day: "Sun", runs: 44, success: 42, failed: 2 },
];

// LLM token usage by LLM (4 configured LLMs)
const llmTokenUsage = [
  { name: "GPT-4", tokens: 12400, color: "#22c55e" },
  { name: "Claude 3", tokens: 8200, color: "#6366f1" },
  { name: "Gemini Pro", tokens: 6100, color: "#eab308" },
  { name: "Llama 3", tokens: 3900, color: "#a855f7" },
];

// Active server logs (errors / warnings from deployed servers)
const serverLogs = [
  { id: 1, server: "api-gateway", level: "error" as const, message: "Connection timeout to workflow engine", time: "2m ago" },
  { id: 2, server: "mcp-server", level: "warn" as const, message: "Rate limit approaching for tenant acme", time: "8m ago" },
  { id: 3, server: "agent-runtime", level: "error" as const, message: "LLM gateway returned 503", time: "14m ago" },
  { id: 4, server: "scheduler", level: "warn" as const, message: "Job weekly-report delayed by 30s", time: "22m ago" },
  { id: 5, server: "api-gateway", level: "error" as const, message: "Health check failed for workflow-engine", time: "31m ago" },
];

// Schedulers: active (idle/waiting) vs running (currently executing)
const schedulerStats = { active: 5, running: 3 };
const schedulerRecentRuns = [
  { name: "Daily backup", status: "completed" as const, at: "06:00" },
  { name: "Weekly report", status: "running" as const, at: "06:15" },
  { name: "Lead sync", status: "completed" as const, at: "05:45" },
  { name: "Invoice reminders", status: "queued" as const, at: "06:30" },
];

// MCP users / usage
const mcpUsersData = [
  { day: "Mon", users: 3, requests: 28 },
  { day: "Tue", users: 4, requests: 45 },
  { day: "Wed", users: 4, requests: 52 },
  { day: "Thu", users: 5, requests: 61 },
  { day: "Fri", users: 5, requests: 58 },
  { day: "Sat", users: 3, requests: 22 },
  { day: "Sun", users: 5, requests: 48 },
];

// Agentic AI: agent runs and completions
const agentRunsData = [
  { day: "Mon", runs: 86, completed: 82 },
  { day: "Tue", runs: 102, completed: 98 },
  { day: "Wed", runs: 94, completed: 90 },
  { day: "Thu", runs: 118, completed: 114 },
  { day: "Fri", runs: 125, completed: 120 },
  { day: "Sat", runs: 72, completed: 69 },
  { day: "Sun", runs: 98, completed: 95 },
];

const agentByType = [
  { name: "Support", runs: 45 },
  { name: "Research", runs: 32 },
  { name: "Code", runs: 28 },
  { name: "Data", runs: 18 },
];

const tooltipStyle = {
  backgroundColor: "#0a0a0a",
  border: "1px solid #262626",
  borderRadius: "12px",
  padding: "12px 16px",
  fontSize: "13px",
  boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
};
const tooltipLabelStyle = { color: "#a3a3a3", marginBottom: 4 };

function Sparkline({ data, height = 28, id }: { data: number[]; height?: number; id: string }) {
  const chartData = data.map((v, i) => ({ i, v }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={ACCENT} stopOpacity={0.5} />
            <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke={ACCENT} strokeWidth={2} fill={`url(#${id})`} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default function Dashboard() {
  const [range, setRange] = useState<"7d" | "30d">("7d");

  return (
    <div className="min-h-screen bg-black text-white font-sans antialiased">
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(99,102,241,0.08),transparent)]" />
      <div className="fixed inset-0 pointer-events-none bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:56px_56px]" />

      <div className="relative p-6 lg:p-8 space-y-8 w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Dashboard</h1>
            <p className="text-sm text-zinc-500 mt-1">Platform overview and metrics</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg bg-white/[0.04] border border-white/[0.08] p-1">
              {(["7d", "30d"] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${range === r ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white"}`}
                >
                  {r === "7d" ? "7 days" : "30 days"}
                </button>
              ))}
            </div>
            <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-zinc-400 hover:text-white hover:bg-white/[0.06] text-sm font-medium transition-all">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <span className="text-xs text-zinc-600 hidden sm:inline">Updated 2m ago</span>
          </div>
        </div>

        {/* Hero stats - full width row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex items-center gap-4 rounded-xl border border-white/[0.08] bg-white/[0.03] px-5 py-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-indigo-500/10 border border-indigo-500/20">
              <UserCheck className="h-5 w-5 text-indigo-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-zinc-500">Active users</p>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-2xl font-bold text-white tabular-nums">14</span>
                <span className="text-xs font-medium text-emerald-400 flex items-center gap-0.5">
                  <ArrowUpRight className="h-3.5 w-3.5" /> 12%
                </span>
              </div>
            </div>
            <div className="hidden sm:block w-16 h-9 shrink-0 opacity-70">
              <Sparkline id="hero-active" data={sparkData.activeUsers} height={36} />
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-xl border border-white/[0.08] bg-white/[0.03] px-5 py-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sky-500/10 border border-sky-500/20">
              <Plug className="h-5 w-5 text-sky-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-zinc-500">MCP users</p>
              <p className="text-2xl font-bold text-white tabular-nums mt-0.5">5</p>
              <p className="text-[11px] text-zinc-500 mt-0.5">With active tokens</p>
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-xl border border-white/[0.08] bg-white/[0.03] px-5 py-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 border border-amber-500/20">
              <CalendarClock className="h-5 w-5 text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-zinc-500">Schedulers</p>
              <div className="flex items-center gap-3 mt-1">
                <div>
                  <span className="text-xl font-bold text-emerald-400 tabular-nums">{schedulerStats.active}</span>
                  <span className="text-[11px] text-zinc-500 ml-1">active</span>
                </div>
                <div className="h-4 w-px bg-white/10" />
                <div>
                  <span className="text-xl font-bold text-amber-400 tabular-nums">{schedulerStats.running}</span>
                  <span className="text-[11px] text-zinc-500 ml-1">running</span>
                </div>
              </div>
              <p className="text-[11px] text-zinc-500 mt-0.5">Always-on jobs</p>
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-xl border border-white/[0.08] bg-white/[0.03] px-5 py-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <MessageCircle className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-zinc-500">Conversations today</p>
              <p className="text-2xl font-bold text-white tabular-nums mt-0.5">86</p>
              <p className="text-[11px] text-zinc-500 mt-0.5">Across all chatbots</p>
            </div>
          </div>
        </div>

        {/* By category */}
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5">
          <h2 className="text-sm font-semibold text-zinc-300 mb-4">By category</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {categoryStats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div
                  key={stat.key}
                  className="flex items-center gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 hover:bg-white/[0.04] hover:border-white/[0.08] transition-colors"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/5 border border-white/10">
                    <Icon className="h-5 w-5 text-zinc-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-lg font-bold text-white tabular-nums leading-tight">{stat.value}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{stat.label}</p>
                    {(stat.trend === "up" || stat.trend === "down") && (
                      <span className={`text-[10px] font-medium mt-0.5 inline-flex items-center gap-0.5 ${stat.trend === "up" ? "text-emerald-400" : "text-red-400"}`}>
                        {stat.trend === "up" ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                        {Math.abs(stat.change)}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Workflow runs (auto) + Schedulers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6">
            <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
              <Workflow className="w-4 h-4 text-zinc-400" />
              Workflow runs (auto)
            </h3>
            <p className="text-xs text-zinc-500 mb-5">Scheduled and triggered runs — not user-triggered</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={workflowRunsData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <XAxis dataKey="day" stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} dy={8} />
                <YAxis stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} width={32} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} cursor={{ stroke: "#3f3f46" }} />
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.04)" vertical={false} />
                <Bar dataKey="runs" name="Total runs" fill={ACCENT} fillOpacity={0.9} radius={[6, 6, 0, 0]} maxBarSize={40} />
                <Bar dataKey="success" name="Success" fill={SUCCESS} fillOpacity={0.9} radius={[6, 6, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6">
            <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-zinc-400" />
              Schedulers
            </h3>
            <p className="text-xs text-zinc-500 mb-4">Active (idle) vs currently running</p>
            <div className="flex gap-4 mb-5">
              <div className="flex-1 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 text-center">
                <p className="text-2xl font-bold text-emerald-400 tabular-nums">{schedulerStats.active}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5 uppercase tracking-wider">Active</p>
              </div>
              <div className="flex-1 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 text-center">
                <p className="text-2xl font-bold text-amber-400 tabular-nums">{schedulerStats.running}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5 uppercase tracking-wider">Running</p>
              </div>
            </div>
            <div className="border-t border-white/[0.06] pt-4">
              <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-2">Recent runs</p>
              <ul className="space-y-2">
                {schedulerRecentRuns.map((r, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-300 truncate">{r.name}</span>
                    <span className={`text-[11px] font-medium shrink-0 ml-2 ${r.status === "completed" ? "text-emerald-500" : r.status === "running" ? "text-amber-500" : "text-zinc-500"}`}>
                      {r.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* LLM token usage (4 LLMs) */}
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6">
          <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
            <Cpu className="w-4 h-4 text-zinc-400" />
            LLM token usage
          </h3>
          <p className="text-xs text-zinc-500 mb-5">Tokens consumed by configured LLMs (7d)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {llmTokenUsage.map((llm) => (
              <div key={llm.name} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <p className="text-xs font-medium text-zinc-400">{llm.name}</p>
                <p className="text-xl font-bold text-white tabular-nums mt-1">{llm.tokens.toLocaleString()}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">tokens</p>
                <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${(llm.tokens / 12400) * 100}%`, backgroundColor: llm.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Active server logs + MCP users chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] overflow-hidden">
            <div className="px-5 py-4 border-b border-white/[0.08] flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Server className="w-4 h-4 text-zinc-400" />
                Active server logs
              </h3>
              <button className="text-xs text-indigo-400 hover:text-indigo-300 font-medium">View all</button>
            </div>
            <p className="px-5 text-[11px] text-zinc-500 pb-2">Errors and warnings from deployed servers</p>
            <ul className="divide-y divide-white/[0.06] max-h-[280px] overflow-y-auto">
              {serverLogs.map((log) => (
                <li key={log.id} className="px-5 py-3 hover:bg-white/[0.03] transition-colors">
                  <div className="flex gap-2 items-start">
                    <span className={`shrink-0 w-2 h-2 rounded-full mt-1.5 ${log.level === "error" ? "bg-red-500" : "bg-amber-500"}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-mono text-zinc-400">{log.server}</p>
                      <p className="text-sm text-zinc-200 mt-0.5">{log.message}</p>
                      <p className="text-[11px] text-zinc-500 mt-1">{log.time}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6">
            <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
              <Plug className="w-4 h-4 text-zinc-400" />
              MCP users & requests
            </h3>
            <p className="text-xs text-zinc-500 mb-5">MCP users and request volume (7d)</p>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={mcpUsersData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="mcpUsersGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ACCENT} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="mcpReqGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={WARN} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={WARN} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} dy={8} />
                <YAxis yAxisId="left" stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} width={28} />
                <YAxis yAxisId="right" orientation="right" stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} width={32} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} cursor={{ stroke: "#3f3f46" }} />
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.04)" vertical={false} />
                <Area yAxisId="left" type="monotone" dataKey="users" name="MCP users" stroke={ACCENT} fill="url(#mcpUsersGrad)" strokeWidth={2} />
                <Line yAxisId="right" type="monotone" dataKey="requests" name="Requests" stroke={WARN} strokeWidth={2} dot={{ fill: WARN, r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Agentic AI charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6">
            <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-zinc-400" />
              Agent runs (agentic AI)
            </h3>
            <p className="text-xs text-zinc-500 mb-5">Agent executions and completions (7d)</p>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={agentRunsData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="agentRunsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={ACCENT} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="agentDoneGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SUCCESS} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={SUCCESS} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} dy={8} />
                <YAxis stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} width={32} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} cursor={{ stroke: "#3f3f46" }} />
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.04)" vertical={false} />
                <Area type="monotone" dataKey="runs" name="Runs" stroke={ACCENT} fill="url(#agentRunsGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="completed" name="Completed" stroke={SUCCESS} fill="url(#agentDoneGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6">
            <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
              <Bot className="w-4 h-4 text-zinc-400" />
              Agents by type
            </h3>
            <p className="text-xs text-zinc-500 mb-5">Runs by agent category</p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={agentByType} layout="vertical" margin={{ left: 0, right: 20 }}>
                <XAxis type="number" stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} width={56} />
                <Tooltip contentStyle={tooltipStyle} />
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.04)" horizontal={false} />
                <Bar dataKey="runs" name="Runs" fill={ACCENT} radius={[0, 6, 6, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* System health */}
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-5">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            System health
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { name: "API", status: "operational" as const, latency: "42ms" },
              { name: "LLM Gateway", status: "degraded" as const, latency: "1.8s" },
              { name: "MCP Server", status: "operational" as const, latency: "28ms" },
              { name: "Workflow Engine", status: "operational" as const, latency: "15ms" },
            ].map((s) => (
              <div key={s.name} className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${s.status === "operational" ? "bg-emerald-500" : "bg-amber-500"}`} />
                  <span className="text-sm font-medium text-zinc-200">{s.name}</span>
                </div>
                <p className="text-xs font-mono text-zinc-500">{s.latency}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


